import createGraph from 'ngraph.graph';
import type {
  BuilderRecipeDetailsFragment,
  RecipeDetailsFragment
} from '../../__generated__/apollo-hooks';
import { isRecipeDetailARecipeDetailFinished } from '../../__generated__/apollo-hooks';
import type { GraphLink, GraphNodeData } from '../../helpers/indexedDb.helper';

type NGraphData = {
  nodes: GraphNodeData[];
  links: GraphLink[];
};

export const createNGraphFromData = (data: NGraphData) => {
  const graph = createGraph();

  data.nodes?.forEach((node) => {
    graph?.addNode(node.id, node.val);
  });

  data.links?.forEach((link) => {
    graph?.addLink(link?.source, link.target);
  });
  return graph;
};

type TopologyRecipeLike =
  | RecipeDetailsFragment
  | BuilderRecipeDetailsFragment
  | null;

type TransformResult = {
  nodes: GraphNodeData[];
  links: GraphLink[];
  rootNodeId?: number;
};

/**
 * Converts a recipe fragment into a flat nodes-and-links representation for
 * the Builder and topology viewer. Two input shapes are supported:
 *
 * 1. BuilderRecipeDetailsFragment with `detail.graph` populated — the
 *    preferred Builder path. One round trip to the server, flat vertex /
 *    edge data with Singleton-deduped references expressed as REFERENCE edges.
 * 2. RecipeDetailsFragment (or BuilderRecipeDetailsFragment with no graph)
 *    with nested `detail.recipeList` / `detail.preconditions` — the legacy
 *    path used by the topology viewer on the recipe details page.
 *
 * Output is the same GraphNodeData[] / GraphLink[] shape in both cases.
 * REFERENCE edges surface as synthetic nodes with `isReference: true` and
 * `referencesNodeId` pointing at the client node that corresponds to the
 * target vertex (i.e. the Singleton's first occurrence).
 */
export function transformRecipeToNodesAndLinks(
  recipe: TopologyRecipeLike,
  offsetId = 0,
  isPrecondition = false
): TransformResult {
  if (
    recipe?.detail &&
    isRecipeDetailARecipeDetailFinished(recipe.detail) &&
    'graph' in recipe.detail &&
    recipe.detail.graph
  ) {
    return transformGraphToNodesAndLinks(
      recipe as BuilderRecipeDetailsFragment,
      offsetId,
      isPrecondition
    );
  }

  return transformNestedRecipeToNodesAndLinks(recipe, offsetId, isPrecondition);
}

function transformGraphToNodesAndLinks(
  recipe: BuilderRecipeDetailsFragment,
  offsetId: number,
  rootIsPrecondition: boolean
): TransformResult {
  const detail = recipe.detail;
  if (
    !detail ||
    !isRecipeDetailARecipeDetailFinished(detail) ||
    !('graph' in detail) ||
    !detail.graph
  ) {
    return { nodes: [], links: [] };
  }
  const { rootVertexId, vertices, edges } = detail.graph;

  // Treat the root vertex as a custom recipe when its bundle is a YAML
  // bundle (the user authored / imported this recipe). Without the flag,
  // `createYamlFromNodesAndLinks` walks past the root and emits an empty
  // YAML on the next round-trip.
  const rootIsCustom = recipe.bundle?.__typename === 'YamlRecipeBundle';

  const vertexById = new Map<number, (typeof vertices)[number]>();
  for (const v of vertices) {
    vertexById.set(v.id, v);
  }

  // Bucket edges by parent server id for deterministic child walks
  const childEdgesByParent = new Map<number, (typeof edges)[number][]>();
  for (const e of edges) {
    const list = childEdgesByParent.get(e.from) ?? [];
    list.push(e);
    childEdgesByParent.set(e.from, list);
  }

  const nodes: GraphNodeData[] = [];
  const links: GraphLink[] = [];
  const vertexIdToNodeId = new Map<number, number>();
  // Track emitted reference nodes so we can resolve referencesNodeId in a
  // second pass after every target has a client id assigned.
  const emittedReferences: Array<{
    clientNodeId: number;
    targetVertexId: number;
  }> = [];
  let nextId = offsetId;

  function walk(
    vertexId: number,
    parentNodeId: number | undefined,
    markAsPrecondition: boolean
  ): void {
    const vertex = vertexById.get(vertexId);
    if (!vertex) {
      return;
    }

    const nodeId = nextId++;
    // `vertex.descriptor` is the thin `BuilderRecipeNodeFields` shape
    // (id, instanceName, name, description, options) — a subset of the wider
    // `RecipeDetailsFragment` that `GraphNodeData.val` is typed as. The
    // cast preserves the existing storage type at the cost of the shape
    // being structurally incomplete: `bundle`, `recipeCount`, `dataTables`,
    // `detail` are NOT populated for nodes produced via this path.
    // Downstream consumers handle that via optional chaining. See
    // `GraphNodeData.val` JSDoc.
    //
    // For the root vertex specifically: prefer the caller-supplied
    // `recipe.options` over `vertex.descriptor.options`. The "Add to
    // Builder" flow stages user-typed option values on `recipe.options`
    // before calling the transform, but the graph's descriptor reflects
    // backend defaults. Without this override the user's values would
    // be silently discarded.
    const isRootVertex = vertexId === rootVertexId;
    const valSource =
      isRootVertex && recipe.options
        ? { ...vertex.descriptor, options: recipe.options }
        : vertex.descriptor;
    nodes.push({
      id: nodeId,
      name: vertex.descriptor.instanceName || vertex.descriptor.name,
      val: { ...valSource } as unknown as GraphNodeData['val'],
      isPrecondition: markAsPrecondition,
      ...(isRootVertex && rootIsCustom ? { isCustomRecipe: true } : {})
    });
    vertexIdToNodeId.set(vertex.id, nodeId);
    if (parentNodeId !== undefined) {
      links.push({ source: parentNodeId, target: nodeId });
    }

    // Preconditions first (matches legacy transformer ordering), then
    // recipe children, then references — preserving server-declared order
    // within each group.
    const children = childEdgesByParent.get(vertex.id) ?? [];
    const preconditionChildren = children.filter(
      (e) => e.type === 'PRECONDITION'
    );
    const recipeChildren = children.filter((e) => e.type === 'RECIPE');
    const referenceChildren = children.filter((e) => e.type === 'REFERENCE');

    for (const e of preconditionChildren) {
      walk(e.to, nodeId, true);
    }
    for (const e of recipeChildren) {
      walk(e.to, nodeId, false);
    }

    // REFERENCE edges point at a Singleton's original occurrence; emit a
    // synthetic leaf so the tree shows a `(reference)` chip, and record the
    // target vertex id for deferred `referencesNodeId` resolution.
    for (const e of referenceChildren) {
      const target = vertexById.get(e.to);
      const displayName = target
        ? target.descriptor.instanceName || target.descriptor.name
        : 'Unknown recipe';
      // Same partial-shape compromise as the canonical vertex path above.
      const val = target
        ? ({ ...target.descriptor } as unknown as GraphNodeData['val'])
        : undefined;
      const refNodeId = nextId++;
      nodes.push({
        id: refNodeId,
        name: displayName,
        val,
        isPrecondition: false,
        isReference: true
      });
      links.push({ source: nodeId, target: refNodeId });
      emittedReferences.push({
        clientNodeId: refNodeId,
        targetVertexId: e.to
      });
    }
  }

  walk(rootVertexId, undefined, rootIsPrecondition);

  // Second pass: resolve each emitted reference's target node id.
  for (const ref of emittedReferences) {
    const targetClientId = vertexIdToNodeId.get(ref.targetVertexId);
    if (targetClientId === undefined) {
      continue;
    }
    const node = nodes.find((n) => n.id === ref.clientNodeId);
    if (node) {
      node.referencesNodeId = targetClientId;
    }
  }

  return { nodes, links, rootNodeId: nodes.at(0)?.id };
}

function transformNestedRecipeToNodesAndLinks(
  recipe: TopologyRecipeLike,
  offsetId: number,
  isPrecondition: boolean
): TransformResult {
  const nodes: GraphNodeData[] = [];
  const links: GraphLink[] = [];
  let uniqueId = offsetId;

  function traverse(
    recipe: TopologyRecipeLike,
    parentId?: number,
    markAsPrecondition = false
  ): void {
    const nodeId = uniqueId++;

    nodes.push({
      id: nodeId,
      name: recipe?.instanceName || recipe?.name,
      val: {
        ...recipe,
        detail: undefined
      } as GraphNodeData['val'],
      isPrecondition:
        markAsPrecondition || (isPrecondition && nodeId === offsetId)
    });

    if (parentId !== undefined) {
      links.push({ source: parentId, target: nodeId });
    }

    if (recipe?.detail && isRecipeDetailARecipeDetailFinished(recipe.detail)) {
      recipe.detail.preconditions?.forEach((subRecipe) =>
        traverse(subRecipe as unknown as TopologyRecipeLike, nodeId, true)
      );

      recipe.detail.recipeList?.edges?.forEach((edge) =>
        traverse(edge.node as unknown as TopologyRecipeLike, nodeId)
      );
    }
  }

  traverse(recipe);

  return { nodes, links, rootNodeId: nodes.at(0)?.id };
}
